import React, { memo, useCallback } from 'react';
import styled, { css } from 'styled-components';

import colors from '@front/common/src/utils/colors';
import { CommonCardProps } from '@front/common/src/types/components';
import { useOnClickThrottled } from '@front/common/src/hooks/use-on-click-throttled';

import { boxShadow, boxShadowOnHover } from '../../styles/mixins';

const disabledStyle = css`
  opacity: 0.6;
`;

const pressableStyle = css`
  cursor: pointer;
  ${boxShadowOnHover};
`;

const StyledCard = styled.div<{
  $color?: string;
  $borderRadius?: number;
  $isPressable: boolean;
  $isDisabled: boolean;
}>`
  display: flex;
  ${boxShadow.normal};
  box-sizing: border-box;
  width: auto;
  outline: none;
  ${({ $color, $borderRadius, $isPressable, $isDisabled }) => css`
    flex-direction: column;
    background-color: ${$color || colors.white100};
    border-radius: ${$borderRadius || 12}px;
    ${$isPressable && pressableStyle}
    ${$isDisabled && disabledStyle}
  `};
`;

export type CardProps = CommonCardProps & {
  ariaDisabled?: boolean;
  ariaSelected?: boolean;
  label?: string;
  className?: string;
  role?: string;
  borderRadius?: number;
};

const Card = ({
  ariaDisabled,
  ariaSelected,
  label,
  onPress,
  selected = false,
  isDisabled = false,
  className,
  role = 'button',
  borderRadius,
  color,
  children,
}: CardProps) => {
  const isPressable = !!onPress && !isDisabled;
  const tabIndexToSet = isPressable ? 0 : undefined;

  const onClickThrottled = useOnClickThrottled(isPressable ? onPress : undefined);
  const onKeyUp = useCallback(
    // @ts-ignore TODO: set typing
    (e) => {
      // Space key
      if (e.keyCode === 32) {
        onClickThrottled();
      }
    },
    [onClickThrottled],
  );

  const roleToSet = onPress ? role : undefined;
  const ariaChecked = (role === 'checkbox' || role === 'radio') && !!onPress ? selected : undefined;

  return (
    <StyledCard
      $isDisabled={isDisabled}
      $isPressable={isPressable}
      $color={color}
      $borderRadius={borderRadius}
      aria-label={label}
      aria-checked={ariaChecked}
      /*
       * testing the presence of aria-disabled using `toBeDisabled` from @testing-library/react does
       * not work, it is only available for form controls https://github.com/testing-library/jest-dom#tobedisabled
       */
      aria-disabled={isDisabled || ariaDisabled}
      aria-selected={ariaSelected}
      onClick={onClickThrottled}
      role={roleToSet}
      className={className}
      tabIndex={tabIndexToSet}
      onKeyUp={onKeyUp}
    >
      {children}
    </StyledCard>
  );
};

export default memo(Card);
